import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaTools, FaCalendarCheck, FaShieldAlt, FaCheckCircle, FaArrowRight, FaPhone, FaServer, FaNetworkWired, FaDatabase } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const PreventiveMaintenancePage = () => {
    const services = [
        {
            icon: <FaServer size={24} />,
            title: 'Server Maintenance',
            description: 'Regular server health checks, updates, and optimization to ensure peak performance.',
            gradient: 'from-teal-500 to-green-500'
        },
        {
            icon: <FaNetworkWired size={24} />,
            title: 'Network Maintenance',
            description: 'Routine network equipment checks, firmware updates, and performance tuning.',
            gradient: 'from-blue-600 to-cyan-600'
        },
        {
            icon: <FaShieldAlt size={24} />,
            title: 'Security Updates',
            description: 'Regular security patching and vulnerability assessments to protect your systems.',
            gradient: 'from-red-500 to-rose-500'
        },
        {
            icon: <FaDatabase size={24} />,
            title: 'Backup Verification',
            description: 'Regular backup testing and validation to ensure data recovery readiness.',
            gradient: 'from-purple-600 to-indigo-600'
        },
        {
            icon: <FaCalendarCheck size={24} />,
            title: 'Scheduled Inspections',
            description: 'Planned maintenance visits to identify potential issues before they become problems.',
            gradient: 'from-orange-500 to-amber-500'
        },
        {
            icon: <FaTools size={24} />,
            title: 'Hardware Cleaning',
            description: 'Physical cleaning and dust removal to extend equipment life and reliability.',
            gradient: 'from-gray-600 to-gray-800'
        }
    ];

    const benefits = [
        'Reduced downtime',
        'Extended equipment life',
        'Lower repair costs',
        'Improved performance',
        'Predictable IT expenses',
        'Peace of mind'
    ];

    const maintenancePlan = [
        { title: 'Monthly', items: ['System health checks', 'Security updates', 'Backup verification', 'Performance monitoring'] },
        { title: 'Quarterly', items: ['Deep system analysis', 'Hardware inspection', 'Capacity planning', 'Documentation update'] },
        { title: 'Annual', items: ['Full infrastructure audit', 'Technology roadmap review', 'Disaster recovery testing', 'Vendor review'] }
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-teal-900 to-green-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-green-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-teal-400">Preventive Maintenance</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-green-400">Preventive</span> Maintenance
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Stop problems before they start. Our proactive maintenance programs keep your IT infrastructure running at peak performance.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-green-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition-all duration-300">
                                    Get Started
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-teal-600/20 to-green-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=600&h=400&fit=crop"
                                    alt="Preventive Maintenance"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Comprehensive Maintenance Services</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Regular maintenance that prevents issues and extends the life of your IT investments
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-teal-200"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Maintenance Plans */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-teal-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">Maintenance Schedule</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Planned Maintenance Activities</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {maintenancePlan.map((plan, index) => (
                            <div key={index} className="bg-white rounded-2xl p-8 shadow-lg">
                                <h3 className="text-xl font-bold text-gray-900 mb-6 pb-4 border-b border-gray-100">{plan.title}</h3>
                                <ul className="space-y-3">
                                    {plan.items.map((item, idx) => (
                                        <li key={idx} className="flex items-center gap-3">
                                            <FaCheckCircle className="text-teal-500 flex-shrink-0" size={16} />
                                            <span className="text-gray-700">{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&h=500&fit=crop"
                                alt="IT Team"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-teal-100 text-teal-700 rounded-full text-sm font-semibold mb-4">Benefits</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Prevention is Better Than Cure</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                A well-maintained IT infrastructure means fewer surprises, lower costs, and better performance.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FaCheckCircle className="text-teal-500 flex-shrink-0" size={20} />
                                        <span className="text-gray-700 font-medium">{benefit}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-teal-500 to-green-500">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Start Your Maintenance Program Today</h2>
                    <p className="text-xl text-teal-100 mb-8">
                        Protect your IT investment with proactive maintenance services.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-teal-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Get Maintenance Quote
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Speak to an Expert
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default PreventiveMaintenancePage;
